import type {
  StoreVoiceOrderConfirmSelection,
  StoreVoiceOrderItem
} from "@repo/api-sdk";

/**
 * Stage-1 (match review) domain logic for voice ordering (KAN-143).
 *
 * The parse response ({@link StoreVoiceOrderItem}[]) is immutable; this module
 * owns the buyer's edits on top of it - the chosen variant for an ambiguous
 * item, the adjusted quantity, whether a line is included - and turns the final
 * picks into the `{variant_id, quantity}` selections the confirm endpoint
 * (KAN-147) resolves live. Kept pure so the include/resolve/quantity rules are
 * unit-tested without rendering the review screen.
 */

/**
 * Per-item editable state, keyed by the item's index in the parse response
 * (stable for a given result). Holds only the buyer's edits, never a copy of
 * the parse item itself.
 */
export interface VoiceReviewItemState {
  /**
   * The variant this line will add, or `null` when nothing is resolved yet:
   * an `exact` item is pre-filled with its resolved variant; an `ambiguous`
   * item stays `null` until the buyer picks an option; a `not_found` item is
   * always `null` (there is nothing to add).
   */
  variantId: string | null;
  quantity: number;
  /** Whether the line is part of the order. `not_found` is never includable. */
  included: boolean;
}

export type VoiceReviewState = Record<number, VoiceReviewItemState>;

/** Every quantity clamps to at least one - removing a line is un-including it. */
export const MIN_VOICE_QUANTITY = 1;

function normalizeQuantity(quantity: number): number {
  const rounded = Math.round(quantity);
  return Number.isFinite(rounded) && rounded > MIN_VOICE_QUANTITY
    ? rounded
    : MIN_VOICE_QUANTITY;
}

/**
 * The starting review state for a fresh parse result: `exact` items included at
 * the spoken quantity with their resolved variant; `ambiguous` items present
 * but not included until the buyer picks (there is no safe default among
 * genuinely different products); `not_found` items never included.
 */
export function initialReviewState(
  items: StoreVoiceOrderItem[]
): VoiceReviewState {
  const state: VoiceReviewState = {};
  items.forEach((item, index) => {
    const quantity = normalizeQuantity(item.quantity);
    if (item.match_type === "exact") {
      state[index] = {
        variantId: item.resolved_product.variant_id,
        quantity,
        included: true
      };
    } else {
      // ambiguous (needs a pick) and not_found (nothing to add) both start out
      // unresolved and excluded.
      state[index] = { variantId: null, quantity, included: false };
    }
  });
  return state;
}

/** Pick an option for an ambiguous item - resolving it also includes it. */
export function selectOption(
  state: VoiceReviewState,
  index: number,
  variantId: string
): VoiceReviewState {
  const current = state[index];
  if (!current) {
    return state;
  }
  return {
    ...state,
    [index]: { ...current, variantId, included: true }
  };
}

/** Un-pick an ambiguous item, returning it to the option picker and excluding it. */
export function clearOption(
  state: VoiceReviewState,
  index: number
): VoiceReviewState {
  const current = state[index];
  if (!current) {
    return state;
  }
  return {
    ...state,
    [index]: { ...current, variantId: null, included: false }
  };
}

/** Set a line's quantity, clamped to at least one. */
export function setQuantity(
  state: VoiceReviewState,
  index: number,
  quantity: number
): VoiceReviewState {
  const current = state[index];
  if (!current) {
    return state;
  }
  return {
    ...state,
    [index]: { ...current, quantity: normalizeQuantity(quantity) }
  };
}

/** Toggle whether a line is included (no-op for a line with no resolved variant). */
export function setIncluded(
  state: VoiceReviewState,
  index: number,
  included: boolean
): VoiceReviewState {
  const current = state[index];
  if (!current || (included && !current.variantId)) {
    return state;
  }
  return {
    ...state,
    [index]: { ...current, included }
  };
}

/** A line contributes to the order when it is included and resolved to a variant. */
export function isResolved(item: VoiceReviewItemState | undefined): boolean {
  return Boolean(item?.included && item.variantId);
}

/** How many lines are currently headed to the cart. */
export function countIncluded(state: VoiceReviewState): number {
  return Object.values(state).filter(isResolved).length;
}

/**
 * Ambiguous items the buyer has not resolved yet - they will be silently
 * dropped on continue, so the UI flags the count rather than proceeding as if
 * they were handled.
 */
export function countUnresolvedAmbiguous(
  items: StoreVoiceOrderItem[],
  state: VoiceReviewState
): number {
  return items.reduce((count, item, index) => {
    if (item.match_type === "ambiguous" && !state[index]?.variantId) {
      return count + 1;
    }
    return count;
  }, 0);
}

/**
 * Ambiguous items the buyer resolved (picked a variant) - the inverse of
 * {@link countUnresolvedAmbiguous}. Counts a pick even if the buyer later
 * unchecked the line, since this measures whether disambiguation succeeded,
 * not whether the line ended up in the cart.
 */
export function countAmbiguousResolved(
  items: StoreVoiceOrderItem[],
  state: VoiceReviewState
): number {
  return items.reduce((count, item, index) => {
    if (item.match_type === "ambiguous" && Boolean(state[index]?.variantId)) {
      return count + 1;
    }
    return count;
  }, 0);
}

/**
 * Exact matches the buyer kept included at commit time. An exact item is
 * pre-resolved and pre-included by {@link initialReviewState}, so the only
 * buyer action that changes this count is unchecking the line.
 */
export function countExactConfirmed(
  items: StoreVoiceOrderItem[],
  state: VoiceReviewState
): number {
  return items.reduce((count, item, index) => {
    if (item.match_type === "exact" && isResolved(state[index])) {
      return count + 1;
    }
    return count;
  }, 0);
}

/**
 * The `{variant_id, quantity}` selections for the confirm call. Only included,
 * resolved lines contribute; duplicate variants (the same product spoken twice)
 * are merged by **summing** quantities, in first-seen order - so "two texture
 * spray ... and one more" adds three, rather than the backend's dedupe silently
 * keeping only the last.
 */
export function buildConfirmSelections(
  state: VoiceReviewState
): StoreVoiceOrderConfirmSelection[] {
  const quantityByVariant = new Map<string, number>();
  const order: string[] = [];

  const indices = Object.keys(state)
    .map(Number)
    .sort((a, b) => a - b);

  for (const index of indices) {
    const item = state[index];
    if (!isResolved(item)) {
      continue;
    }
    const variantId = item.variantId as string;
    const quantity = normalizeQuantity(item.quantity);
    if (quantityByVariant.has(variantId)) {
      quantityByVariant.set(
        variantId,
        quantityByVariant.get(variantId)! + quantity
      );
    } else {
      quantityByVariant.set(variantId, quantity);
      order.push(variantId);
    }
  }

  return order.map((variant_id) => ({
    variant_id,
    quantity: quantityByVariant.get(variant_id)!
  }));
}
